import { useEffect, useState } from "react";
import {
  App,
  Col,
  Modal,
  Row,
  Space,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { jsPDF } from "jspdf";
import { db, storage } from "../lib/firebase";
import { listUsers, streamAi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApexLayout } from "./apex/ApexProvider";
import type {
  MetricItem,
  Stage,
  SubtaskItem,
  TaskItem,
  TaskStatus,
} from "../lib/types";
import OverviewTab from "./projectDetails/OverviewTab";
import DetailsTab from "./projectDetails/DetailsTab";
import PlanTab from "./projectDetails/PlanTab";
import MetricsTab from "./projectDetails/MetricsTab";
import CommentsTab from "./projectDetails/CommentsTab";
import AttachmentsTab from "./projectDetails/AttachmentsTab";
import TaskDrawer from "./projectDetails/TaskDrawer";
import SubtaskDrawer from "./projectDetails/SubtaskDrawer";
import {
  getStatusColor,
  type AttachmentItem,
  type CommentItem,
  type ProjectItem,
  type TabType,
  type UserOption,
  type Vendor,
} from "./projectDetails/types";

interface ProjectDetailsModalProps {
  project: ProjectItem;
  tasks: TaskItem[];
  metrics: MetricItem[];
  subtasks: SubtaskItem[];
  comments: CommentItem[];
  attachments: AttachmentItem[];
  onClose: () => void;
  onUpdateProject: (updates: Partial<ProjectItem>) => Promise<void>;
}

export default function ProjectDetailsModal({
  project,
  tasks,
  metrics,
  subtasks,
  comments,
  attachments,
  onClose,
  onUpdateProject,
}: ProjectDetailsModalProps) {
  const { message, modal } = App.useApp();
  const { user } = useAuth();
  // Clip the modal + its mask so they don't paint over the Apex side panel.
  const { drawerOpen: apexOpen, apexWidth, pushMode } = useApexLayout();
  const apexOffset = apexOpen && pushMode ? apexWidth : 0;
  const [activeTab, setActiveTab] = useState<TabType>("summary");
  const [viewingTask, setViewingTask] = useState<TaskItem | null>(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [metricModalOpen, setMetricModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null);
  const [editingMetric, setEditingMetric] = useState<MetricItem | null>(null);
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    dependencies: "",
    stageId: "",
    startDate: "",
    endDate: "",
  });
  const [metricForm, setMetricForm] = useState({
    title: "",
    description: "",
    dueDate: "",
    dependencies: "",
  });
  const [subtaskCommentDrafts, setSubtaskCommentDrafts] = useState<Record<string, string>>({});
  const [selectedSubtask, setSelectedSubtask] = useState<SubtaskItem | null>(null);
  const [taskComment, setTaskComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [summaryRefreshing, setSummaryRefreshing] = useState(false);
  const [summaryPreview, setSummaryPreview] = useState("");

  useEffect(() => {
    const stagesQuery = query(
      collection(db, "projectStages"),
      where("projectId", "==", project.id),
      orderBy("order", "asc"),
    );
    const unsubscribe = onSnapshot(stagesQuery, (snapshot) => {
      const data: Stage[] = [];
      snapshot.forEach((stageDoc) => {
        const raw = stageDoc.data();
        data.push({
          id: stageDoc.id,
          projectId: raw.projectId,
          name: raw.name ?? "",
          order: typeof raw.order === "number" ? raw.order : 0,
          createdAt: raw.createdAt ?? "",
        });
      });
      setStages(data);
    });
    return () => unsubscribe();
  }, [project.id]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "vendorsPublic"), (snapshot) => {
      const vendorData: Vendor[] = [];
      snapshot.forEach((vendorDoc) => {
        const data = vendorDoc.data();
        if (data.status === "active") {
          vendorData.push({ id: vendorDoc.id, ...data } as Vendor);
        }
      });
      vendorData.sort((a, b) => {
        const nameA = a.profile?.split("\n")[0] || "";
        const nameB = b.profile?.split("\n")[0] || "";
        return nameA.localeCompare(nameB);
      });
      setVendors(vendorData);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await listUsers();
        setUsers(usersData);
      } catch (error) {
        console.error("Failed to load users:", error);
      }
    };
    loadUsers();
  }, []);

  const totalSubtasks = subtasks.length;
  const completedSubtasks = subtasks.filter((subtask) => subtask.status === "done").length;
  const overallProgress = totalSubtasks
    ? Math.round((completedSubtasks / totalSubtasks) * 100)
    : 0;

  const updateAiSummary = async () => {
    setSummaryRefreshing(true);
    setSummaryPreview("");
    try {
      const taskSummaries = tasks
        .map((task) => {
          const subtaskList = subtasks.filter((s) => s.taskId === task.id);
          const completed = subtaskList.filter((s) => s.status === "done").length;
          const percent = subtaskList.length
            ? Math.round((completed / subtaskList.length) * 100)
            : 0;
          const dates = [
            task.startDate ? `Start ${task.startDate}` : null,
            task.endDate ? `End ${task.endDate}` : null,
          ]
            .filter(Boolean)
            .join(", ");
          return `- ${task.title} (${percent}% complete)${dates ? ` [${dates}]` : ""}${
            task.dependencies ? ` [Dependencies: ${task.dependencies}]` : ""
          }`;
        })
        .join("\n");

      const metricSummaries = metrics
        .map(
          (metric) =>
            `- ${metric.title}${metric.dueDate ? ` (Due ${metric.dueDate})` : ""}${
              metric.dependencies ? ` [Deps: ${metric.dependencies}]` : ""
            }`,
        )
        .join("\n");

      const subtaskSummaries = subtasks
        .map(
          (subtask) =>
            `- ${subtask.title} (${subtask.status})${
              subtask.assignedTo ? ` [${subtask.assignedTo}]` : ""
            }${subtask.dependencies ? ` [Deps: ${subtask.dependencies}]` : ""}`,
        )
        .join("\n");

      const recentComments = comments
        .slice(-5)
        .map((c) => `- ${c.message}`)
        .join("\n");

      const context = `Project: ${project.name}\nSource: ${project.source || "Unknown"}\nStatus: ${project.status}\n\nTasks (${tasks.length}): ${taskSummaries}\n\nMetrics (${metrics.length}): ${metricSummaries}\n\nSubtasks (${subtasks.length}): ${subtaskSummaries}\n\nRecent Comments (${comments.length}): ${recentComments}\n\nAttachments: ${attachments.length} files`;

      // OAIP-91: ask for JSON {summary, headline} so we can populate both
      // `aiSummary` (long-form, surfaced in the Overview tab) and `aiHeadline`
      // (1-sentence, surfaced in the Leadership PowerPoint tables) in one
      // model round-trip.
      const { text, meta } = await streamAi(
        'Generate a concise summary of this project. Return JSON ONLY in this shape: { "summary": "<under 200 words on current state, key progress, risks/blockers, recommended next steps>", "headline": "<one short sentence, <=120 chars, plain language, suitable for a leadership-meeting agenda bullet>" }. No preamble, no code fences.',
        context,
        (chunk) => setSummaryPreview((prev) => `${prev}${chunk}`),
        {
          sessionId: crypto.randomUUID(),
          thinkingLevel: "LOW",
        },
      );

      const rawText = meta?.message || text || "";
      let aiSummary = "Summary not available.";
      let aiHeadline: string | undefined;
      try {
        const parsed = JSON.parse(rawText);
        if (parsed && typeof parsed === "object") {
          if (typeof parsed.summary === "string" && parsed.summary.trim()) {
            aiSummary = parsed.summary.trim();
          }
          if (typeof parsed.headline === "string" && parsed.headline.trim()) {
            aiHeadline = parsed.headline.trim().slice(0, 300);
          }
        }
      } catch {
        // Model occasionally drifts off JSON — fall back to using the whole
        // response as the summary and leaving aiHeadline untouched.
        if (rawText.trim()) aiSummary = rawText;
      }

      const updates: Record<string, unknown> = {
        aiSummary,
        aiSummaryUpdatedAt: new Date().toISOString(),
      };
      if (aiHeadline) {
        updates.aiHeadline = aiHeadline;
        updates.aiHeadlineUpdatedAt = new Date().toISOString();
      }
      await updateDoc(doc(db, "projects", project.id), updates);
    } catch (error) {
      console.error("Failed to update AI summary:", error);
    } finally {
      setSummaryRefreshing(false);
      setSummaryPreview("");
    }
  };

  const handleUpdateProject = async (updates: Partial<ProjectItem>) => {
    await onUpdateProject(updates);
    updateAiSummary();
  };

  const handleUpdateProjectWithConfirm = async (updates: Partial<ProjectItem>) => {
    if (updates.status && updates.status !== project.status) {
      modal.confirm({
        title: "Update project status",
        content: `Change project status to "${updates.status}"?`,
        okText: "Update",
        onOk: async () => {
          await onUpdateProject(updates);
          message.success("Project status updated");
        },
      });
    } else {
      await onUpdateProject(updates);
    }
  };

  const syncTaskCompletion = async (taskId: string, nextSubtasks: SubtaskItem[]) => {
    const taskSubtasks = nextSubtasks.filter((subtask) => subtask.taskId === taskId);
    const task = tasks.find((item) => item.id === taskId);
    if (taskSubtasks.length === 0) {
      if (task?.completedAt) {
        await updateDoc(doc(db, "projectTasks", taskId), { completedAt: null });
      }
      return;
    }
    const allDone = taskSubtasks.every((subtask) => subtask.status === "done");
    if (allDone && !task?.completedAt) {
      await updateDoc(doc(db, "projectTasks", taskId), {
        completedAt: new Date().toISOString(),
      });
    } else if (!allDone && task?.completedAt) {
      await updateDoc(doc(db, "projectTasks", taskId), { completedAt: null });
    }
  };

  const createNotification = async (payload: {
    userId: string;
    message: string;
    projectId?: string;
    taskId?: string;
    type?: string;
  }) => {
    await addDoc(collection(db, "notifications"), {
      userId: payload.userId,
      message: payload.message,
      projectId: payload.projectId || null,
      taskId: payload.taskId || null,
      type: payload.type || "task_assignment",
      read: false,
      createdAt: new Date().toISOString(),
      createdBy: user?.uid || null,
    });
  };

  const notifyAssigneeChange = async (
    subtask: SubtaskItem,
    assigneeId: string,
    action: "assigned" | "reassigned",
  ) => {
    if (!assigneeId) return;
    await createNotification({
      userId: assigneeId,
      message: `You were ${action} to subtask "${subtask.title}" in ${project.name}.`,
      projectId: project.id,
      taskId: subtask.id,
      type: "task_assignment",
    });
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim() || !taskForm.stageId) return;
    if (editingTask) {
      await updateDoc(doc(db, "projectTasks", editingTask.id), {
        title: taskForm.title.trim(),
        description: taskForm.description?.trim() || "",
        dependencies: taskForm.dependencies?.trim() || "",
        stageId: taskForm.stageId,
        startDate: taskForm.startDate || null,
        endDate: taskForm.endDate || null,
      });
    } else {
      await addDoc(collection(db, "projectTasks"), {
        projectId: project.id,
        stageId: taskForm.stageId,
        title: taskForm.title.trim(),
        description: taskForm.description?.trim() || "",
        dependencies: taskForm.dependencies?.trim() || "",
        startDate: taskForm.startDate || null,
        endDate: taskForm.endDate || null,
        completedAt: null,
        comments: [],
        createdAt: new Date().toISOString(),
      });
    }
    setTaskModalOpen(false);
    setEditingTask(null);
    setTaskForm({
      title: "",
      description: "",
      dependencies: "",
      stageId: "",
      startDate: "",
      endDate: "",
    });
    updateAiSummary();
  };

  const handleDeleteTask = async (task: TaskItem) => {
    modal.confirm({
      title: "Delete task",
      content: "This will delete the task and all its subtasks. Continue?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const taskSubtasks = subtasks.filter((subtask) => subtask.taskId === task.id);
        await Promise.all(
          taskSubtasks.map((subtask) =>
            deleteDoc(doc(db, "taskSubtasks", subtask.id)),
          ),
        );
        await deleteDoc(doc(db, "projectTasks", task.id));
        updateAiSummary();
      },
    });
  };

  const handleUpdateSubtask = async (
    subtask: SubtaskItem,
    updates: Partial<SubtaskItem>,
  ) => {
    await updateDoc(doc(db, "taskSubtasks", subtask.id), updates);
    if (updates.assignedTo && updates.assignedTo !== subtask.assignedTo) {
      await notifyAssigneeChange(
        subtask,
        updates.assignedTo,
        subtask.assignedTo ? "reassigned" : "assigned",
      );
    }
    updateAiSummary();
  };

  const handleUpdateSubtaskStatus = async (
    subtask: SubtaskItem,
    status: TaskStatus,
  ) => {
    await updateDoc(doc(db, "taskSubtasks", subtask.id), {
      status,
      completedAt: status === "done" ? new Date().toISOString() : null,
    });
    await syncTaskCompletion(
      subtask.taskId,
      subtasks.map((item) => (item.id === subtask.id ? { ...item, status } : item)),
    );
    updateAiSummary();
  };

  const handleDeleteSubtask = async (subtask: SubtaskItem) => {
    modal.confirm({
      title: "Delete subtask",
      content: "Are you sure you want to delete this subtask?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await deleteDoc(doc(db, "taskSubtasks", subtask.id));
        await syncTaskCompletion(
          subtask.taskId,
          subtasks.filter((item) => item.id !== subtask.id),
        );
        updateAiSummary();
      },
    });
  };

  const openNewTaskModal = () => {
    setEditingTask(null);
    setTaskForm({
      title: "",
      description: "",
      dependencies: "",
      stageId: "",
      startDate: "",
      endDate: "",
    });
    setTaskModalOpen(true);
  };

  const openEditTaskModal = (task: TaskItem) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description || "",
      dependencies: task.dependencies || "",
      stageId: task.stageId || "",
      startDate: task.startDate || "",
      endDate: task.endDate || "",
    });
    setTaskModalOpen(true);
  };

  const openNewMetricModal = () => {
    setEditingMetric(null);
    setMetricForm({ title: "", description: "", dueDate: "", dependencies: "" });
    setMetricModalOpen(true);
  };

  const openEditMetricModal = (metric: MetricItem) => {
    setEditingMetric(metric);
    setMetricForm({
      title: metric.title,
      description: metric.description || "",
      dueDate: metric.dueDate || "",
      dependencies: metric.dependencies || "",
    });
    setMetricModalOpen(true);
  };

  const handleAddSubtaskComment = async (subtaskId: string) => {
    const draft = subtaskCommentDrafts[subtaskId] || "";
    if (!draft.trim()) return;
    await updateDoc(doc(db, "taskSubtasks", subtaskId), {
      comments: arrayUnion({
        id: crypto.randomUUID(),
        message: draft.trim(),
        createdAt: new Date().toISOString(),
        authorId: user?.uid || "",
        authorName: user?.displayName || user?.email || "User",
      }),
    });
    setSubtaskCommentDrafts((prev) => ({ ...prev, [subtaskId]: "" }));
    updateAiSummary();
  };

  const handleAddTaskCommentToViewing = async () => {
    if (!taskComment.trim() || !viewingTask) return;
    const taskRef = doc(db, "projectTasks", viewingTask.id);
    await updateDoc(taskRef, {
      comments: arrayUnion({
        id: crypto.randomUUID(),
        message: taskComment,
        createdAt: new Date().toISOString(),
        authorId: user?.uid || null,
        authorName: user?.displayName || user?.email || "User",
      }),
    });
    setTaskComment("");
    message.success("Comment added");
  };

  const handleSaveMetric = async () => {
    if (!metricForm.title.trim()) return;
    if (editingMetric) {
      await updateDoc(doc(db, "projectMetrics", editingMetric.id), {
        title: metricForm.title.trim(),
        description: metricForm.description?.trim() || "",
        dependencies: metricForm.dependencies?.trim() || "",
        dueDate: metricForm.dueDate || null,
      });
    } else {
      await addDoc(collection(db, "projectMetrics"), {
        projectId: project.id,
        title: metricForm.title.trim(),
        description: metricForm.description?.trim() || "",
        dependencies: metricForm.dependencies?.trim() || "",
        dueDate: metricForm.dueDate || null,
        completedAt: null,
        createdAt: new Date().toISOString(),
      });
    }
    setMetricModalOpen(false);
    setEditingMetric(null);
    setMetricForm({ title: "", description: "", dueDate: "", dependencies: "" });
    updateAiSummary();
  };

  const handleDeleteMetric = async (metricId: string) => {
    modal.confirm({
      title: "Delete metric",
      content: "Are you sure you want to delete this metric?",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        await deleteDoc(doc(db, "projectMetrics", metricId));
        updateAiSummary();
      },
    });
  };

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const storageRef = ref(storage, `projects/${project.id}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await addDoc(collection(db, "attachments"), {
        projectId: project.id,
        name: file.name,
        url,
        type: file.type,
        createdAt: new Date().toISOString(),
      });
      updateAiSummary();
      message.success("File uploaded successfully.");
    } catch (error) {
      console.error("Failed to upload file:", error);
      message.error("Failed to upload file. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadSummary = () => {
    const pdfDoc = new jsPDF();
    let y = 16;
    const lineHeight = 7;

    const addLine = (text: string) => {
      pdfDoc.text(text, 14, y);
      y += lineHeight;
    };

    const addSection = (title: string) => {
      y += 2;
      pdfDoc.setFont("helvetica", "bold");
      addLine(title);
      pdfDoc.setFont("helvetica", "normal");
    };

    addLine(`Project Summary: ${project.name}`);
    addLine(`Type: ${project.type?.replace(/-/g, " ") ?? "—"}`);
    addLine(`Status: ${project.status}`);
    addLine(`Start Date: ${project.startDate || "—"}`);
    addLine(`End Date: ${project.endDate || "—"}`);
    addLine(`Overall Progress: ${overallProgress}%`);

    addSection("Metrics");
    if (metrics.length === 0) {
      addLine("No metrics recorded.");
    } else {
      metrics.forEach((metric) => {
        addLine(`- ${metric.title} (Due: ${metric.dueDate || "—"})`);
        if (metric.dependencies) {
          addLine(`  Dependencies: ${metric.dependencies}`);
        }
      });
    }

    addSection("Tasks");
    if (tasks.length === 0) {
      addLine("No tasks recorded.");
    } else {
      // Group tasks by stage in stage.order order; "Unstaged" bucket at end.
      const stageIds = new Set(stages.map((stage) => stage.id));
      const buckets: Array<{ title: string; tasks: TaskItem[] }> = stages.map(
        (stage) => ({ title: stage.name, tasks: [] }),
      );
      const unstaged: TaskItem[] = [];
      tasks.forEach((task) => {
        if (task.stageId && stageIds.has(task.stageId)) {
          const bucket = buckets.find(
            (b) =>
              b.title === stages.find((s) => s.id === task.stageId)?.name,
          );
          if (bucket) bucket.tasks.push(task);
        } else {
          unstaged.push(task);
        }
      });
      if (unstaged.length > 0) {
        buckets.push({ title: "Unstaged", tasks: unstaged });
      }

      buckets.forEach((bucket) => {
        if (bucket.tasks.length === 0) return;
        addLine(`Stage: ${bucket.title}`);
        bucket.tasks.forEach((task) => {
          const taskSubtasks = subtasks.filter(
            (subtask) => subtask.taskId === task.id,
          );
          const completed = taskSubtasks.filter(
            (subtask) => subtask.status === "done",
          ).length;
          const percent = taskSubtasks.length
            ? Math.round((completed / taskSubtasks.length) * 100)
            : 0;
          addLine(
            `  ${task.title} - ${completed}/${taskSubtasks.length} (${percent}%)`,
          );
          taskSubtasks.forEach((subtask) => {
            addLine(
              `    - ${subtask.title} [${subtask.status}] (Due: ${
                subtask.dueDate || "—"
              })`,
            );
            if (subtask.dependencies) {
              addLine(`      Dependencies: ${subtask.dependencies}`);
            }
          });
        });
      });
    }

    addSection("AI Summary");
    const summaryText = project.aiSummary || "No AI summary available.";
    const summaryLines = pdfDoc.splitTextToSize(summaryText, 180);
    summaryLines.forEach((line: string) => addLine(line));

    pdfDoc.save(`${project.name}-summary.pdf`);
  };

  return (
    <>
      <Modal
        title="Project Details"
        open
        onCancel={onClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
        styles={{
          body: {
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingRight: 8,
          },
          mask: { right: apexOffset },
          wrapper: { right: apexOffset },
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Typography.Text type="secondary">
              {project.type?.replace(/-/g, " ") ?? "—"}
            </Typography.Text>
            <Typography.Title level={3} style={{ marginBottom: 0 }}>
              {project.name}
            </Typography.Title>
            <Space wrap size="small" style={{ marginTop: 8 }}>
              <Tag color={getStatusColor(project.status)}>{project.status}</Tag>
            </Space>
          </div>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as TabType)}
                items={[
                  {
                    key: "summary",
                    label: "AI Summary",
                    children: (
                      <OverviewTab
                        project={project}
                        tasks={tasks}
                        metrics={metrics}
                        subtasks={subtasks}
                        summaryRefreshing={summaryRefreshing}
                        summaryPreview={summaryPreview}
                        onRefreshSummary={updateAiSummary}
                        onDownloadSummary={handleDownloadSummary}
                      />
                    ),
                  },
                  {
                    key: "details",
                    label: "Project Details",
                    children: (
                      <DetailsTab
                        project={project}
                        vendors={vendors}
                        onUpdate={handleUpdateProject}
                        onUpdateWithConfirm={handleUpdateProjectWithConfirm}
                      />
                    ),
                  },
                  {
                    key: "plan",
                    label: "Project Plan",
                    children: (
                      <PlanTab
                        projectId={project.id}
                        tasks={tasks}
                        subtasks={subtasks}
                        users={users}
                        taskForm={taskForm}
                        setTaskForm={setTaskForm}
                        editingTask={editingTask}
                        taskModalOpen={taskModalOpen}
                        onOpenNewTask={openNewTaskModal}
                        onOpenEditTask={openEditTaskModal}
                        onSaveTask={handleSaveTask}
                        onDeleteTask={handleDeleteTask}
                        onSelectTask={(task) => setViewingTask(task)}
                        onCloseTaskModal={() => setTaskModalOpen(false)}
                      />
                    ),
                  },
                  {
                    key: "metrics",
                    label: "Metrics",
                    children: (
                      <MetricsTab
                        metrics={metrics}
                        metricForm={metricForm}
                        setMetricForm={setMetricForm}
                        editingMetric={editingMetric}
                        metricModalOpen={metricModalOpen}
                        onOpenNewMetric={openNewMetricModal}
                        onOpenEditMetric={openEditMetricModal}
                        onSaveMetric={handleSaveMetric}
                        onDeleteMetric={handleDeleteMetric}
                        onCloseMetricModal={() => setMetricModalOpen(false)}
                      />
                    ),
                  },
                  {
                    key: "comments",
                    label: "Comments",
                    children: <CommentsTab comments={comments} />,
                  },
                  {
                    key: "files",
                    label: "Files",
                    children: (
                      <AttachmentsTab
                        attachments={attachments}
                        uploading={uploading}
                        onUpload={handleUpload}
                      />
                    ),
                  },
                ]}
              />
            </Col>
          </Row>
        </Space>
      </Modal>

      <SubtaskDrawer
        subtask={
          selectedSubtask
            ? subtasks.find((s) => s.id === selectedSubtask.id) || selectedSubtask
            : null
        }
        project={project}
        users={users}
        subtaskCommentDrafts={subtaskCommentDrafts}
        setSubtaskCommentDrafts={setSubtaskCommentDrafts}
        onClose={() => setSelectedSubtask(null)}
        onUpdateSubtask={handleUpdateSubtask}
        onUpdateSubtaskStatus={handleUpdateSubtaskStatus}
        onAddSubtaskComment={handleAddSubtaskComment}
      />

      <TaskDrawer
        task={viewingTask}
        project={project}
        subtasks={subtasks}
        users={users}
        taskComment={taskComment}
        setTaskComment={setTaskComment}
        onClose={() => setViewingTask(null)}
        onSelectSubtask={(subtask) => setSelectedSubtask(subtask)}
        onUpdateSubtaskStatus={handleUpdateSubtaskStatus}
        onEditSubtask={(subtask) => setSelectedSubtask(subtask)}
        onDeleteSubtask={handleDeleteSubtask}
        onSubmitTaskComment={handleAddTaskCommentToViewing}
      />
    </>
  );
}
